import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Icon,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupAddon,
  Switch,
  Row,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from '@polichat/flamboyant';
import { useGlotaT } from '../../../../hooks/useGlotaTranslation';

import PageMainHeader from '../../../common/pages/PageMainHeader/PageMainHeader.component';
import PageMainBody from '../../../common/pages/PageMainBody/PageMainBody.component';
import Animation from '../../../common/animation';

import history from '../../../../services/history';
import useNectar from './useNectar';
import useModal from './useModal';

import { useIntegrationService } from '../useIntegrationService';

import {
  StatusContainer,
  ButtonsContainer,
  LabelContainer,
  EmailItem,
  EmailsContainer,
} from './NectarIntegration.styles';
import integrations from '../integrations-driver';
import poliConfig from '../../../../utils/poliConfig';
import { IconPlusIcon } from '../../../../assets/illustratorElement/iconPlusIcon/iconPlusIcon';

function NectarIntegration({ data }) {
  const [nectarData, setNectarData] = useState(data);
  
  // Traduções
  const integrationsTitle = useGlotaT('spa.integrations.title');
  const nectarTitle = useGlotaT('spa.integrations.nectar.title');
  const tokenLabel = useGlotaT('spa.integrations.nectar.tokenLabel');
  const tokenPlaceholder = useGlotaT('spa.integrations.nectar.tokenPlaceholder');
  const emailLabel = useGlotaT('spa.integrations.nectar.emailLabel');
  const emailPlaceholder = useGlotaT('spa.integrations.nectar.emailPlaceholder');
  const useAttendantEmailLabel = useGlotaT('spa.integrations.nectar.useAttendantEmail');
  const useAttendantEmailInfo = useGlotaT('spa.integrations.nectar.useAttendantEmailInfo');
  const emailsAdded = useGlotaT('spa.integrations.nectar.emailsAdded');
  const disconnect = useGlotaT('spa.integrations.nectar.disconnect');
  const disconnectTooltip = useGlotaT('spa.integrations.nectar.disconnectTooltip');
  const confirm = useGlotaT('spa.integrations.nectar.confirm');
  const cancel = useGlotaT('spa.integrations.nectar.cancel');
  const removeEmailTitleTemplate = useGlotaT('spa.integrations.nectar.removeEmailTitle');
  const removeEmailCancel = useGlotaT('spa.integrations.nectar.removeEmailCancel');
  const removeEmailDelete = useGlotaT('spa.integrations.nectar.removeEmailDelete');
  const disconnectTitle = useGlotaT('spa.integrations.nectar.disconnectTitle');
  const disconnectCancel = useGlotaT('spa.integrations.nectar.disconnectCancel');
  const disconnectDelete = useGlotaT('spa.integrations.nectar.disconnectDelete');
  const insertToken = useGlotaT('spa.integrations.nectar.insertToken');
  const insertEmail = useGlotaT('spa.integrations.nectar.insertEmail');

  const { isOpen, onOpenRequest, onCloseRequest, selected } = useModal();
  const {
    isOpen: isOpenDisconnect,
    onOpenRequest: onOpenDisconnect,
    onCloseRequest: onCloseDisconnect,
  } = useModal();

  const {
    nectarStatus,
    nectarToken,
    handleToken,
    nectarEmails,
    nectarEmailInput,
    setNectarEmailInput,
    handleAddEmail,
    handleRemoveEmail,
    useAttendantEmail,
    handleToggleUseAttendantEmail,
  } = useNectar({ nectarData: nectarData });

  const {
    isLoading,
    isError,
    isSuccess,
    isDeleted,
    saveIntegrationData,
    deleteIntegrationData,
  } = useIntegrationService();

  const handleSwitchDisconnect = () => {
    deleteIntegrationData(integrations.NECTAR);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    history.goBack();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    let nectarParams = {
      driver: integrations.NECTAR,
      token: nectarToken,
      config: {
        useAttendantEmail: useAttendantEmail,
        emails: nectarEmails,
      },
    };

    if (!nectarParams.token.length) {
      toast.warning(insertToken);
      return;
    }
    if (!nectarParams.config.emails.length) {
      toast.warning(insertEmail);
      return;
    }

    saveIntegrationData(nectarParams);
  };

  const handleTextAlert = () => {
    if (!useAttendantEmail) {
      return;
    }
    return (
      <Row>
        <Col>
          <Label>
            {useAttendantEmailInfo}
          </Label>
        </Col>
      </Row>
    );
  };
  const shouldUseAttendantEmail = () => {
    return (
      <>
        <Row>
          <Col>
            <FormGroup>
              <Switch
                id="try-email-nectarcrm"
                isChecked={useAttendantEmail}
                onClick={(e) => handleToggleUseAttendantEmail()}
              ></Switch>
              <Label for="try-email-nectarcrm">
                {useAttendantEmailLabel}
              </Label>
            </FormGroup>
          </Col>
        </Row>
        {handleTextAlert()}
      </>
    );
  };

  useEffect(() => {
    //request post response error
    if (isError) {
      setNectarData({ error: isError });
    }
    //request post response success
    if (isSuccess) {
      setNectarData({ success: isSuccess });
    }
  }, [isError, isSuccess]);

  useEffect(() => {
    if (isDeleted) {
      history.goBack();
    }
  }, [isDeleted]);

  return (
    <>
      <PageMainHeader
        title={integrationsTitle}
        icon="poli-icon pi-integrations-line"
      />
      <PageMainBody>
        <StatusContainer color={nectarStatus.statusColor}>
          <IconPlusIcon
            firstIcon="onlyLogo"
            secondIcon="nectarIcon"
            secondColor="var(--success)"
          />
          {isLoading ? (
            <Animation icon="chat-loading" />
          ) : (
            <>
              <h2>{nectarStatus.message?.[0] || nectarTitle}</h2>
              <h3>{nectarStatus.message?.[1] || ''}</h3>
            </>
          )}
        </StatusContainer>
        <Form onSubmit={handleSubmit} className="col">
          <Row>
            <Col>
              <FormGroup>
                <LabelContainer>
                  <Icon
                    icon="poli-icon pi-key-line"
                    size={26}
                    color="var(--primary)"
                  />
                  <Label for="token-nectarcrm">{tokenLabel}</Label>
                </LabelContainer>
                <Input
                  type="text"
                  id="token-nectarcrm"
                  name="nectarToken"
                  value={nectarToken}
                  onChange={(e) => handleToken(e.target.value)}
                  placeholder={tokenPlaceholder}
                  required
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <FormGroup>
                <LabelContainer>
                  <Icon
                    icon="poli-icon pi-mail-line"
                    size={26}
                    color="var(--primary)"
                  />
                  <Label>{emailLabel}</Label>
                </LabelContainer>
                <InputGroup>
                  <Input
                    id="search"
                    type="email"
                    placeholder={emailPlaceholder}
                    value={nectarEmailInput}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddEmail(nectarEmailInput);
                      }
                    }}
                    onChange={(e) => setNectarEmailInput(e.target.value)}
                  />
                  <InputGroupAddon addonType="append">
                    <Button
                      gradient="primary"
                      onClick={() => handleAddEmail(nectarEmailInput)}
                    >
                      <Icon
                        position="relative"
                        top="2px"
                        icon="poli-icon pi-add-circle-fill"
                        size={20}
                        color={'white'}
                      />
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </FormGroup>
            </Col>
          </Row>
          {shouldUseAttendantEmail()}
          <Row>
            <Col>
              <span>
                <strong>{emailsAdded} </strong>
              </span>
              <EmailsContainer>
                {nectarEmails?.map((email, index) => (
                  <EmailItem key={index}>
                    <span>{email}</span>
                    <span>
                      <Icon
                        icon="poli-icon pi-delete-line"
                        color="var(--danger)"
                        // onClick={() => handleRemoveEmail(email)}
                        onClick={() => onOpenRequest(email)}
                      />
                    </span>
                  </EmailItem>
                ))}
              </EmailsContainer>
            </Col>
          </Row>
          {nectarStatus.status === 'on' || nectarStatus.status === 'error' ? (
            <>
              <Row style={{ marginTop: '2rem' }}>
                <Col>
                  <FormGroup>
                    <span>
                      <strong>{disconnect}</strong>
                    </span>
                    <Button
                      color="danger"
                      size={'normal-icon'}
                      title={disconnectTooltip}
                      id="button-tooltip"
                      onClick={onOpenDisconnect}
                    >
                      <Icon
                        position={'relative'}
                        top={'-5px'}
                        right={'5px'}
                        size={22}
                        icon={'poli-icon pi-integrations-line'}
                      />
                    </Button>
                    <UncontrolledTooltip
                      placement="top"
                      target="button-tooltip"
                    >
                      {disconnectTooltip}
                    </UncontrolledTooltip>
                  </FormGroup>
                </Col>
              </Row>
            </>
          ) : null}
          <Row>
            <ButtonsContainer>
              <Button
                style={{
                  cursor:
                    nectarEmails.length == 0 || nectarEmailInput
                      ? 'not-allowed'
                      : 'default',
                }}
                gradient="primary"
                width="quarter"
                title="confirmar"
                disabled={
                  nectarEmails.length == 0 || nectarEmailInput ? true : false
                }
              >
                {confirm}
              </Button>
              <Button color="danger" onClick={handleCancel}>
                {cancel}
              </Button>
            </ButtonsContainer>
          </Row>
        </Form>
      </PageMainBody>
      {/* Remove e-mail confirmation */}
      <Modal isOpen={isOpen} toggle={onCloseRequest}>
        <ModalBody>{removeEmailTitleTemplate.replace(':email', selected)}</ModalBody>
        <ModalFooter>
          <Button outline color="primary" onClick={onCloseRequest}>
            {removeEmailCancel}
          </Button>
          <Button
            color="danger"
            onClick={() => {
              handleRemoveEmail(selected);
              onCloseRequest();
            }}
          >
            {removeEmailDelete}
          </Button>
        </ModalFooter>
      </Modal>
      {/* Remove connection confirmation */}
      <Modal isOpen={isOpenDisconnect} toggle={onCloseDisconnect}>
        <ModalBody>{disconnectTitle}</ModalBody>
        <ModalFooter>
          <Button outline color="primary" onClick={onCloseDisconnect}>
            {disconnectCancel}
          </Button>
          <Button color="danger" onClick={handleSwitchDisconnect}>
            {disconnectDelete}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default NectarIntegration;
